import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './CSS/HomeBuilder.css';
import {
  blockDefaults,
  blockTextStyle,
  buildDefaultSections,
  moveItem,
  SECTION_LABELS,
} from '../utils/homeLayout';

const EMPTY_SETTINGS = { mainMedia: [], title: '', subtitle: '', sections: [] };

const blankMedia = () => ({ type: 'image', url: '', videoLink: '', videoLinkText: 'Try it yourself' });

// The HTML5 drag & drop API does not fire on touch, so on a phone the arrow
// buttons are the only way to reorder - say so instead of inviting a drag that
// will not work.
const IS_TOUCH = typeof window !== 'undefined'
  && window.matchMedia?.('(pointer: coarse)').matches;

// Blocks that are not tied to a category, offered at the top of the add menu.
const CONTENT_BLOCKS = [
  { type: 'banner', hint: 'Wide image or video with a button' },
  { type: 'productRow', hint: 'Pick the products by hand' },
  { type: 'richText', hint: 'A heading and some copy' },
  { type: 'spacer', hint: 'Blank space or a divider' },
];

/**
 * Home page block builder.
 *
 * Left column: the ordered list of blocks (drag to reorder, eye to hide, click
 * to edit) plus the inspector for the selected block. Right column: a live
 * preview built from the same data the Home page renders, so what you drag is
 * what you get.
 *
 * Reordering uses the native HTML5 drag & drop API - no extra dependency - with
 * the arrow buttons as a keyboard/touch-friendly fallback.
 */
const ManageDashboard = ({ backendUrl, resetKey }) => {
  const [settings, setSettings] = useState(EMPTY_SETTINGS);
  const [savedSnapshot, setSavedSnapshot] = useState(JSON.stringify(EMPTY_SETTINGS));
  const [catalog, setCatalog] = useState([]);          // every category + product count
  const [products, setProducts] = useState([]);        // published products, for picked rows and links
  const [landingPages, setLandingPages] = useState([]);
  const [previewData, setPreviewData] = useState([]);  // categories with their products
  const [selectedId, setSelectedId] = useState(null);
  const [isDefaultLayout, setIsDefaultLayout] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const dragIndex = useRef(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const inspectorRef = useRef(null);
  const skipScrollRef = useRef(true);

  const sections = useMemo(() => settings.sections || [], [settings.sections]);
  const isDirty = JSON.stringify(settings) !== savedSnapshot;

  const flash = useCallback((type, text) => {
    setStatus({ type, text });
    setTimeout(() => setStatus(null), 4000);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      const token = localStorage.getItem('auth-token');

      try {
        const [settingsRes, blocksRes, previewRes] = await Promise.all([
          fetch(`${backendUrl}/dashboard-settings`),
          fetch(`${backendUrl}/dashboard-settings/available-blocks`, {
            headers: { 'Content-Type': 'application/json', 'auth-token': token },
          }),
          fetch(`${backendUrl}/category/categories-with-products`),
        ]);

        const settingsData = await settingsRes.json();
        const blocksData = await blocksRes.json();
        const previewJson = await previewRes.json();
        if (cancelled) return;

        const categories = blocksData.success ? blocksData.categories : [];
        setCatalog(categories);
        setProducts(blocksData.success ? blocksData.products || [] : []);
        setLandingPages(blocksData.success ? blocksData.landingPages || [] : []);
        setPreviewData(previewJson.success ? previewJson.categories : []);

        // The layout comes from available-blocks, not from the public endpoint:
        // that one strips hidden blocks, and the builder needs them all.
        const loaded = {
          mainMedia: settingsData.settings?.mainMedia || [],
          title: settingsData.settings?.title || '',
          subtitle: settingsData.settings?.subtitle || '',
          sections: blocksData.sections?.length
            ? blocksData.sections
            : buildDefaultSections(categories),
        };

        setSettings(loaded);
        setSavedSnapshot(JSON.stringify(loaded));
        setIsDefaultLayout(!!blocksData.sectionsAreDefault);
        setSelectedId(loaded.sections[0]?.id || null);
      } catch (err) {
        console.error('Error loading home layout', err);
        if (!cancelled) flash('error', 'Could not load the home layout.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [backendUrl, resetKey, flash]);

  const selected = sections.find((s) => s.id === selectedId) || null;

  // On a narrow screen the inspector sits below the whole block list, so picking
  // a block would silently change something off-screen. Bring it into view -
  // except for the selection made while loading, which nobody asked for.
  useEffect(() => {
    if (skipScrollRef.current) {
      if (selectedId) skipScrollRef.current = false;
      return;
    }
    if (!selectedId || !inspectorRef.current) return;
    if (window.innerWidth > 1100) return;
    inspectorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [selectedId]);

  const categoryById = useMemo(() => {
    const map = new Map();
    catalog.forEach((cat) => map.set(String(cat._id), cat));
    return map;
  }, [catalog]);

  const productById = useMemo(() => {
    const map = new Map();
    products.forEach((product) => map.set(String(product._id), product));
    return map;
  }, [products]);

  const productsByCategoryId = useMemo(() => {
    const map = new Map();
    previewData.forEach((cat) => map.set(String(cat._id), cat.products || []));
    return map;
  }, [previewData]);

  const usedCategoryIds = useMemo(
    () => new Set(sections.filter((s) => s.type === 'categoryRow').map((s) => String(s.categoryId))),
    [sections]
  );

  const hasHero = sections.some((s) => s.type === 'hero');
  const availableCategories = catalog.filter((cat) => !usedCategoryIds.has(String(cat._id)));

  // --- mutations -----------------------------------------------------------

  const setSections = (next) => setSettings((prev) => ({ ...prev, sections: next }));

  const patchSection = (id, patch) =>
    setSections(sections.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const removeSection = (id) => {
    setSections(sections.filter((s) => s.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const appendBlock = (block) => {
    setSections([...sections, block]);
    setSelectedId(block.id);
    setShowAddMenu(false);
  };

  const addHeroBlock = () => {
    const block = blockDefaults.hero();
    setSections([block, ...sections]);
    setSelectedId(block.id);
    setShowAddMenu(false);
  };

  const reorder = (from, to) => setSections(moveItem(sections, from, to));

  const resetToDefault = () => {
    if (!window.confirm('Replace the current layout with the default one (hero + every category)?')) return;
    const next = buildDefaultSections(catalog);
    setSections(next);
    setSelectedId(next[0]?.id || null);
  };

  const discardChanges = () => {
    const restored = JSON.parse(savedSnapshot);
    setSettings(restored);
    setSelectedId(restored.sections[0]?.id || null);
  };

  // --- hero media ----------------------------------------------------------

  const patchMedia = (index, field, value) =>
    setSettings((prev) => ({
      ...prev,
      mainMedia: prev.mainMedia.map((m, i) => (i === index ? { ...m, [field]: value } : m)),
    }));

  const addMedia = () =>
    setSettings((prev) => ({ ...prev, mainMedia: [...prev.mainMedia, blankMedia()] }));

  const removeMedia = (index) =>
    setSettings((prev) => ({ ...prev, mainMedia: prev.mainMedia.filter((_, i) => i !== index) }));

  const moveMedia = (from, to) =>
    setSettings((prev) => ({ ...prev, mainMedia: moveItem(prev.mainMedia, from, to) }));

  // --- picked product rows -------------------------------------------------

  const addPickedProduct = (id) => {
    if (!id || (selected.productIds || []).includes(id)) return;
    patchSection(selected.id, { productIds: [...(selected.productIds || []), id] });
  };

  const removePickedProduct = (index) =>
    patchSection(selected.id, { productIds: selected.productIds.filter((_, i) => i !== index) });

  const movePickedProduct = (from, to) =>
    patchSection(selected.id, { productIds: moveItem(selected.productIds, from, to) });

  // --- save ----------------------------------------------------------------

  const saveSettings = async () => {
    try {
      const token = localStorage.getItem('auth-token');
      const res = await fetch(`${backendUrl}/dashboard-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'auth-token': token },
        body: JSON.stringify(settings),
      });
      const result = await res.json();

      if (result.success) {
        const persisted = {
          mainMedia: result.settings.mainMedia || [],
          title: result.settings.title || '',
          subtitle: result.settings.subtitle || '',
          sections: result.settings.sections || [],
        };
        setSettings(persisted);
        setSavedSnapshot(JSON.stringify(persisted));
        setIsDefaultLayout(false);
        flash('ok', 'Home layout saved.');
      } else {
        flash('error', result.message || 'Error saving the layout.');
      }
    } catch (err) {
      console.error(err);
      flash('error', 'Error saving the layout.');
    }
  };

  // --- drag & drop ---------------------------------------------------------

  const onDragStart = (index) => (e) => {
    dragIndex.current = index;
    e.dataTransfer.effectAllowed = 'move';
    // Firefox needs data on the transfer for the drag to start at all.
    e.dataTransfer.setData('text/plain', String(index));
  };

  const onDragOver = (index) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== index) setDragOverIndex(index);
  };

  const onDrop = (index) => (e) => {
    e.preventDefault();
    if (dragIndex.current !== null) reorder(dragIndex.current, index);
    dragIndex.current = null;
    setDragOverIndex(null);
  };

  const onDragEnd = () => {
    dragIndex.current = null;
    setDragOverIndex(null);
  };

  // --- labels --------------------------------------------------------------

  const blockLabel = (section) => {
    switch (section.type) {
      case 'hero': return 'Hero slider';
      case 'categoryRow': {
        const cat = categoryById.get(String(section.categoryId));
        const name = cat?.displayName || section.categoryName || 'Missing category';
        return section.titleOverride ? `${section.titleOverride} (${name})` : name;
      }
      case 'productRow': return section.titleOverride || 'Picked products';
      case 'banner': return section.heading || 'Banner';
      case 'richText': return section.heading || 'Text';
      default: return SECTION_LABELS[section.type] || section.type;
    }
  };

  const blockSubLabel = (section) => {
    switch (section.type) {
      case 'hero': {
        const n = settings.mainMedia.length;
        return `${n} slide${n === 1 ? '' : 's'}`;
      }
      case 'categoryRow': {
        const cat = categoryById.get(String(section.categoryId));
        if (!cat) return 'Category no longer exists';
        return `Category - ${cat.productCount} product${cat.productCount === 1 ? '' : 's'}${section.featuredStyle ? ', big cards' : ''}`;
      }
      case 'productRow': {
        const n = (section.productIds || []).length;
        return `Picked - ${n} product${n === 1 ? '' : 's'}`;
      }
      case 'banner': return `Banner - ${section.height} ${section.mediaType}`;
      case 'richText': return `Text - ${section.contentFormat === 'html' ? 'HTML' : 'plain'}`;
      case 'spacer': return `Spacer - ${section.spacerHeight}px${section.divider ? ', divider' : ''}`;
      default: return '';
    }
  };

  // --- preview -------------------------------------------------------------

  const renderPreviewStrip = (items, featured) => (
    <div className={`hb-preview-strip${featured ? ' is-featured' : ''}`}>
      {items.slice(0, 10).map((product, i) => (
        <div key={`${product._id}-${i}`} className="hb-preview-card">
          {product.featuredImage
            ? <img src={product.featuredImage} alt={product.title} />
            : <div className="hb-preview-card-placeholder">{product.title}</div>}
        </div>
      ))}
    </div>
  );

  const renderPreviewBody = (section) => {
    switch (section.type) {
      case 'hero': {
        const media = settings.mainMedia[0];
        const isColor = media?.url?.startsWith('#');
        return (
          <div className="hb-preview-hero">
            {media?.url && isColor && (
              <div className="hb-preview-hero-media" style={{ backgroundColor: media.url }} />
            )}
            {media?.url && !isColor && media.type === 'video' && (
              <video className="hb-preview-hero-media" src={media.url} muted loop autoPlay playsInline />
            )}
            {media?.url && !isColor && media.type === 'image' && (
              <img className="hb-preview-hero-media" src={media.url} alt="" />
            )}
            {!media?.url && <div className="hb-preview-hero-media hb-preview-empty">No media yet</div>}
            <div className="hb-preview-hero-text">
              <strong>{settings.title || 'Title'}</strong>
              <span>{settings.subtitle || 'Subtitle'}</span>
            </div>
          </div>
        );
      }

      case 'categoryRow': {
        const cat = categoryById.get(String(section.categoryId));
        const rowProducts = productsByCategoryId.get(String(section.categoryId)) || [];
        return (
          <>
            <h4 className="hb-preview-row-title">
              {section.titleOverride || cat?.displayName || section.categoryName}
            </h4>
            {rowProducts.length === 0 ? (
              <div className="hb-preview-empty-row">
                This category has no published products, so the row will not show on the Home page.
              </div>
            ) : renderPreviewStrip(rowProducts, section.featuredStyle)}
          </>
        );
      }

      case 'productRow': {
        const picked = (section.productIds || []).map((id) => productById.get(String(id))).filter(Boolean);
        return (
          <>
            <h4 className="hb-preview-row-title">{section.titleOverride || 'Untitled row'}</h4>
            {picked.length === 0 ? (
              <div className="hb-preview-empty-row">
                No products picked yet, so the row will not show on the Home page.
              </div>
            ) : renderPreviewStrip(picked, section.featuredStyle)}
          </>
        );
      }

      case 'banner':
        return (
          <div className={`hb-preview-banner hb-preview-banner-${section.height} hb-preview-banner-${section.align}`}>
            {section.mediaType === 'image' && section.mediaUrl && (
              <img className="hb-preview-banner-media" src={section.mediaUrl} alt="" />
            )}
            {section.mediaType === 'video' && section.mediaUrl && (
              <video className="hb-preview-banner-media" src={section.mediaUrl} muted loop autoPlay playsInline />
            )}
            {(section.mediaType === 'color' || !section.mediaUrl) && (
              <div className="hb-preview-banner-media" style={{ backgroundColor: section.backgroundColor }} />
            )}
            <div className="hb-preview-banner-shade" style={{ opacity: section.overlay }} />
            <div className="hb-preview-banner-content">
              {section.heading && <strong>{section.heading}</strong>}
              {section.body && <span>{section.body}</span>}
              {section.buttonText && <em className="hb-preview-banner-btn">{section.buttonText}</em>}
              {/* Never invent copy here: a placeholder heading in the preview
                  reads as real content and gets published as-is. */}
              {!section.heading && !section.body && !section.buttonText && (
                <span className="hb-preview-placeholder">Background only, no text</span>
              )}
            </div>
          </div>
        );

      case 'richText':
        return (
          <div className={`hb-preview-text hb-preview-text-${section.align}`}>
            <div style={{ maxWidth: `${section.maxWidth}px` }}>
              {section.heading && <strong>{section.heading}</strong>}
              {section.content
                ? <p>{section.content.replace(/<[^>]*>/g, ' ').slice(0, 320)}</p>
                : !section.heading && (
                  <span className="hb-preview-placeholder">
                    Empty, it will not show on the Home page
                  </span>
                )}
            </div>
          </div>
        );

      case 'spacer':
        return (
          <div className="hb-preview-spacer" style={{ height: `${Math.min(section.spacerHeight, 120)}px` }}>
            {section.divider && <span />}
          </div>
        );

      default:
        return null;
    }
  };

  const renderPreviewBlock = (section, index) => {
    const hidden = section.enabled === false;
    const overlaps = sections[index - 1]?.type === 'hero' && section.type !== 'hero';

    return (
      <div
        key={section.id}
        className={[
          'hb-preview-block',
          hidden ? 'is-hidden' : '',
          section.id === selectedId ? 'is-selected' : '',
          overlaps ? 'overlaps-hero' : '',
        ].filter(Boolean).join(' ')}
        onClick={() => setSelectedId(section.id)}
        // Same custom properties the real blocks get, so colour and size choices
        // show up here instead of only after saving.
        style={blockTextStyle(section)}
      >
        {renderPreviewBody(section)}
        {hidden && <span className="hb-hidden-tag">Hidden on the site</span>}
      </div>
    );
  };

  // --- inspector -----------------------------------------------------------

  /**
   * Text colour and size, shared by every block that renders text. An empty
   * textColor means "inherit the theme", which is why there is a Default button
   * next to the picker: <input type="color"> can never express "unset".
   */
  const renderTextStyleFields = (section) => (
    <>
      <div className="hb-subtitle">Text</div>

      <label>Colour</label>
      <div className="hb-color-row">
        <input
          type="color"
          className="hb-color"
          value={section.textColor || '#ffffff'}
          onChange={(e) => patchSection(section.id, { textColor: e.target.value })}
        />
        <button
          type="button"
          className="hb-btn-ghost hb-btn-small"
          disabled={!section.textColor}
          onClick={() => patchSection(section.id, { textColor: '' })}
        >
          {section.textColor ? 'Reset' : 'Theme'}
        </button>
      </div>

      <label>
        Size
        <small>
          {section.textScale === 1 || !section.textScale
            ? ' (default)'
            : ` (${Math.round(section.textScale * 100)}%)`}
        </small>
      </label>
      <input
        type="range"
        min="0.5"
        max="2.5"
        step="0.05"
        value={section.textScale ?? 1}
        onChange={(e) => patchSection(section.id, { textScale: Number(e.target.value) })}
      />
    </>
  );

  const renderInspector = () => {
    if (selected.type === 'hero') {
      return (
        <div className="hb-fields">
          <label>Title</label>
          <input value={settings.title} onChange={(e) => setSettings({ ...settings, title: e.target.value })} />

          <label>Subtitle</label>
          <input value={settings.subtitle} onChange={(e) => setSettings({ ...settings, subtitle: e.target.value })} />

          {renderTextStyleFields(selected)}

          <div className="hb-subtitle">Slides</div>
          {settings.mainMedia.map((media, i) => (
            <div className="hb-media" key={i}>
              <div className="hb-media-head">
                <strong>Slide {i + 1}</strong>
                <span>
                  <button type="button" disabled={i === 0} onClick={() => moveMedia(i, i - 1)}>↑</button>
                  <button type="button" disabled={i === settings.mainMedia.length - 1} onClick={() => moveMedia(i, i + 1)}>↓</button>
                  <button type="button" className="hb-danger" onClick={() => removeMedia(i)}>✕</button>
                </span>
              </div>

              <label>Type</label>
              <select value={media.type} onChange={(e) => patchMedia(i, 'type', e.target.value)}>
                <option value="image">Image</option>
                <option value="video">Video</option>
              </select>

              <label>URL <small>(a #hex colour works as a plain background)</small></label>
              <input value={media.url} onChange={(e) => patchMedia(i, 'url', e.target.value)} />

              <label>Button link <small>(optional)</small></label>
              <input
                value={media.videoLink || ''}
                onChange={(e) => patchMedia(i, 'videoLink', e.target.value)}
                placeholder="Leave empty to hide the button"
              />

              <label>Button text</label>
              <input
                value={media.videoLinkText || ''}
                onChange={(e) => patchMedia(i, 'videoLinkText', e.target.value)}
                placeholder="Try it yourself"
              />
            </div>
          ))}
          <button type="button" className="hb-btn-add" onClick={addMedia}>+ Add slide</button>
        </div>
      );
    }

    if (selected.type === 'categoryRow') {
      return (
        <div className="hb-fields">
          <label>Category</label>
          <select
            value={selected.categoryId}
            onChange={(e) => {
              const cat = categoryById.get(e.target.value);
              patchSection(selected.id, { categoryId: e.target.value, categoryName: cat ? cat.name : '' });
            }}
          >
            {!categoryById.has(String(selected.categoryId)) && (
              <option value={selected.categoryId}>{selected.categoryName || 'Missing category'}</option>
            )}
            {catalog.map((cat) => (
              <option
                key={cat._id}
                value={cat._id}
                disabled={usedCategoryIds.has(String(cat._id)) && String(cat._id) !== String(selected.categoryId)}
              >
                {cat.displayName} ({cat.productCount})
              </option>
            ))}
          </select>

          <label>Row title <small>(leave empty to use the category name)</small></label>
          <input
            value={selected.titleOverride || ''}
            onChange={(e) => patchSection(selected.id, { titleOverride: e.target.value })}
            placeholder={categoryById.get(String(selected.categoryId))?.displayName || ''}
          />

          {renderTextStyleFields(selected)}

          <label className="hb-check">
            <input
              type="checkbox"
              checked={!!selected.featuredStyle}
              onChange={(e) => patchSection(selected.id, { featuredStyle: e.target.checked })}
            />
            Big cards (featured style)
          </label>

          <label className="hb-check">
            <input
              type="checkbox"
              checked={selected.enabled !== false}
              onChange={(e) => patchSection(selected.id, { enabled: e.target.checked })}
            />
            Visible on the site
          </label>
        </div>
      );
    }

    if (selected.type === 'productRow') {
      const picked = selected.productIds || [];
      return (
        <div className="hb-fields">
          <label>Row title</label>
          <input
            value={selected.titleOverride || ''}
            onChange={(e) => patchSection(selected.id, { titleOverride: e.target.value })}
            placeholder="New releases"
          />

          <div className="hb-subtitle">Products <small>({picked.length})</small></div>
          <ul className="hb-picked">
            {picked.map((id, i) => {
              const product = productById.get(String(id));
              return (
                <li key={`${id}-${i}`}>
                  {product?.featuredImage && <img src={product.featuredImage} alt="" />}
                  <span>{product?.title || 'Product no longer published'}</span>
                  <span className="hb-block-buttons">
                    <button type="button" disabled={i === 0} onClick={() => movePickedProduct(i, i - 1)}>↑</button>
                    <button type="button" disabled={i === picked.length - 1} onClick={() => movePickedProduct(i, i + 1)}>↓</button>
                    <button type="button" className="hb-danger" onClick={() => removePickedProduct(i)}>✕</button>
                  </span>
                </li>
              );
            })}
            {picked.length === 0 && <li className="hb-picked-empty">No products yet.</li>}
          </ul>

          <label>Add a product</label>
          <select value="" onChange={(e) => addPickedProduct(e.target.value)}>
            <option value="">Pick a product...</option>
            {products
              .filter((p) => !picked.includes(String(p._id)))
              .map((p) => <option key={p._id} value={p._id}>{p.title}</option>)}
          </select>

          {renderTextStyleFields(selected)}

          <label className="hb-check">
            <input
              type="checkbox"
              checked={!!selected.featuredStyle}
              onChange={(e) => patchSection(selected.id, { featuredStyle: e.target.checked })}
            />
            Big cards (featured style)
          </label>

          <label className="hb-check">
            <input
              type="checkbox"
              checked={selected.enabled !== false}
              onChange={(e) => patchSection(selected.id, { enabled: e.target.checked })}
            />
            Visible on the site
          </label>
        </div>
      );
    }

    if (selected.type === 'banner') {
      return (
        <div className="hb-fields">
          <label>Heading</label>
          <input value={selected.heading || ''} onChange={(e) => patchSection(selected.id, { heading: e.target.value })} />

          <label>Text</label>
          <textarea
            rows={3}
            value={selected.body || ''}
            onChange={(e) => patchSection(selected.id, { body: e.target.value })}
          />

          {renderTextStyleFields(selected)}

          <div className="hb-subtitle">Background</div>
          <label>Type</label>
          <select value={selected.mediaType} onChange={(e) => patchSection(selected.id, { mediaType: e.target.value })}>
            <option value="image">Image</option>
            <option value="video">Video</option>
            <option value="color">Flat colour</option>
          </select>

          {selected.mediaType === 'color' ? (
            <>
              <label>Colour</label>
              <input
                type="color"
                className="hb-color"
                value={selected.backgroundColor || '#141414'}
                onChange={(e) => patchSection(selected.id, { backgroundColor: e.target.value })}
              />
            </>
          ) : (
            <>
              <label>URL <small>(must be http(s) or start with /)</small></label>
              <input
                value={selected.mediaUrl || ''}
                onChange={(e) => patchSection(selected.id, { mediaUrl: e.target.value })}
                placeholder="https://..."
              />
            </>
          )}

          <label>Dark overlay <small>({Math.round((selected.overlay ?? 0.4) * 100)}%)</small></label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={selected.overlay ?? 0.4}
            onChange={(e) => patchSection(selected.id, { overlay: Number(e.target.value) })}
          />

          <div className="hb-subtitle">Button</div>
          <label>Button text <small>(empty = no button)</small></label>
          <input
            value={selected.buttonText || ''}
            onChange={(e) => patchSection(selected.id, { buttonText: e.target.value })}
            placeholder="Watch now"
          />

          <label>Links to</label>
          <select
            value={selected.linkType}
            onChange={(e) => patchSection(selected.id, { linkType: e.target.value, linkValue: '' })}
          >
            <option value="none">Nothing</option>
            <option value="product">A product</option>
            <option value="landing">A landing page</option>
            <option value="url">A custom URL</option>
          </select>

          {selected.linkType === 'product' && (
            <select value={selected.linkValue || ''} onChange={(e) => patchSection(selected.id, { linkValue: e.target.value })}>
              <option value="">Pick a product...</option>
              {products.map((p) => <option key={p._id} value={p._id}>{p.title}</option>)}
            </select>
          )}

          {selected.linkType === 'landing' && (
            <select value={selected.linkValue || ''} onChange={(e) => patchSection(selected.id, { linkValue: e.target.value })}>
              <option value="">Pick a landing page...</option>
              {landingPages.map((lp) => <option key={lp.slug} value={lp.slug}>{lp.title} (/{lp.slug})</option>)}
            </select>
          )}

          {selected.linkType === 'url' && (
            <input
              value={selected.linkValue || ''}
              onChange={(e) => patchSection(selected.id, { linkValue: e.target.value })}
              placeholder="https://... or /some-page"
            />
          )}

          <div className="hb-subtitle">Layout</div>
          <label>Height</label>
          <select value={selected.height} onChange={(e) => patchSection(selected.id, { height: e.target.value })}>
            <option value="small">Small</option>
            <option value="medium">Medium</option>
            <option value="large">Large</option>
          </select>

          <label>Text alignment</label>
          <select value={selected.align} onChange={(e) => patchSection(selected.id, { align: e.target.value })}>
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>

          <label className="hb-check">
            <input
              type="checkbox"
              checked={selected.enabled !== false}
              onChange={(e) => patchSection(selected.id, { enabled: e.target.checked })}
            />
            Visible on the site
          </label>
        </div>
      );
    }

    if (selected.type === 'richText') {
      return (
        <div className="hb-fields">
          <label>Heading <small>(optional)</small></label>
          <input value={selected.heading || ''} onChange={(e) => patchSection(selected.id, { heading: e.target.value })} />

          <label>Format</label>
          <select
            value={selected.contentFormat}
            onChange={(e) => patchSection(selected.id, { contentFormat: e.target.value })}
          >
            <option value="text">Plain text</option>
            <option value="html">HTML</option>
          </select>

          <label>
            Content
            <small>
              {selected.contentFormat === 'html'
                ? ' (only formatting tags survive saving - scripts and handlers are stripped)'
                : ' (blank line = new paragraph)'}
            </small>
          </label>
          <textarea
            rows={8}
            value={selected.content || ''}
            onChange={(e) => patchSection(selected.id, { content: e.target.value })}
          />

          <label>Text alignment</label>
          <select value={selected.align} onChange={(e) => patchSection(selected.id, { align: e.target.value })}>
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>

          <label>Max width <small>({selected.maxWidth}px)</small></label>
          <input
            type="range"
            min="320"
            max="1600"
            step="20"
            value={selected.maxWidth}
            onChange={(e) => patchSection(selected.id, { maxWidth: Number(e.target.value) })}
          />

          {renderTextStyleFields(selected)}

          <label className="hb-check">
            <input
              type="checkbox"
              checked={selected.enabled !== false}
              onChange={(e) => patchSection(selected.id, { enabled: e.target.checked })}
            />
            Visible on the site
          </label>
        </div>
      );
    }

    // spacer
    return (
      <div className="hb-fields">
        <label>Height <small>({selected.spacerHeight}px)</small></label>
        <input
          type="range"
          min="8"
          max="400"
          step="4"
          value={selected.spacerHeight}
          onChange={(e) => patchSection(selected.id, { spacerHeight: Number(e.target.value) })}
        />

        <label className="hb-check">
          <input
            type="checkbox"
            checked={!!selected.divider}
            onChange={(e) => patchSection(selected.id, { divider: e.target.checked })}
          />
          Show a divider line
        </label>

        <label className="hb-check">
          <input
            type="checkbox"
            checked={selected.enabled !== false}
            onChange={(e) => patchSection(selected.id, { enabled: e.target.checked })}
          />
          Visible on the site
        </label>
      </div>
    );
  };

  if (loading) {
    return <div className="form-group"><h2>Home Layout</h2><p>Loading...</p></div>;
  }

  return (
    <div className="home-builder">
      <div className="hb-header">
        <div>
          <h2>Home Layout</h2>
          <p className="hb-hint">
            {IS_TOUCH
              ? 'Use the arrows to reorder the blocks, tap one to edit it, use the eye to hide it without deleting.'
              : 'Drag the blocks to reorder them, click one to edit it, use the eye to hide it without deleting.'}
          </p>
        </div>
        <div className="hb-actions">
          {isDirty && <span className="hb-dirty">Unsaved changes</span>}
          <button type="button" className="hb-btn-ghost" onClick={resetToDefault}>Reset to default</button>
          <button type="button" className="hb-btn-ghost" onClick={discardChanges} disabled={!isDirty}>Discard</button>
          <button type="button" className="hb-btn-primary" onClick={saveSettings} disabled={!isDirty}>Save layout</button>
        </div>
      </div>

      {status && <div className={`hb-status hb-status-${status.type}`}>{status.text}</div>}

      {isDefaultLayout && (
        <div className="hb-status hb-status-info">
          No layout has been saved yet, so this is the default one (hero first, then every
          category). Save to make it the layout of record.
        </div>
      )}

      <div className="hb-columns">
        <div className="hb-side">
          <div className="hb-panel">
            <div className="hb-panel-title">Blocks</div>

            <ul className="hb-block-list">
              {sections.map((section, index) => (
                <li
                  key={section.id}
                  className={[
                    'hb-block',
                    section.id === selectedId ? 'is-selected' : '',
                    section.enabled === false ? 'is-disabled' : '',
                    dragOverIndex === index ? 'is-dragover' : '',
                  ].filter(Boolean).join(' ')}
                  draggable
                  onDragStart={onDragStart(index)}
                  onDragOver={onDragOver(index)}
                  onDrop={onDrop(index)}
                  onDragEnd={onDragEnd}
                  onClick={() => setSelectedId(section.id)}
                >
                  <span className="hb-grip" title="Drag to reorder">⠿</span>
                  <span className="hb-block-text">
                    <strong>{blockLabel(section)}</strong>
                    <small>{blockSubLabel(section)}</small>
                  </span>
                  <span className="hb-block-buttons">
                    <button
                      type="button"
                      title="Move up"
                      disabled={index === 0}
                      onClick={(e) => { e.stopPropagation(); reorder(index, index - 1); }}
                    >↑</button>
                    <button
                      type="button"
                      title="Move down"
                      disabled={index === sections.length - 1}
                      onClick={(e) => { e.stopPropagation(); reorder(index, index + 1); }}
                    >↓</button>
                    <button
                      type="button"
                      title={section.enabled === false ? 'Show on the site' : 'Hide from the site'}
                      onClick={(e) => { e.stopPropagation(); patchSection(section.id, { enabled: section.enabled === false }); }}
                    >{section.enabled === false ? '🚫' : '👁'}</button>
                    <button
                      type="button"
                      className="hb-danger"
                      title="Remove block"
                      onClick={(e) => { e.stopPropagation(); removeSection(section.id); }}
                    >✕</button>
                  </span>
                </li>
              ))}
              {sections.length === 0 && (
                <li className="hb-block hb-block-empty">No blocks yet. Add one below.</li>
              )}
            </ul>

            <div className="hb-add">
              <button type="button" className="hb-btn-add" onClick={() => setShowAddMenu(!showAddMenu)}>
                + Add block
              </button>
              {showAddMenu && (
                <div className="hb-add-menu">
                  <div className="hb-add-group">Content</div>
                  {!hasHero && (
                    <button type="button" onClick={addHeroBlock}>
                      Hero slider <small>Top video / image carousel</small>
                    </button>
                  )}
                  {CONTENT_BLOCKS.map(({ type, hint }) => (
                    <button type="button" key={type} onClick={() => appendBlock(blockDefaults[type]())}>
                      {SECTION_LABELS[type]} <small>{hint}</small>
                    </button>
                  ))}

                  <div className="hb-add-group">Category rows</div>
                  {availableCategories.map((cat) => (
                    <button type="button" key={cat._id} onClick={() => appendBlock(blockDefaults.categoryRow(cat))}>
                      {cat.displayName}
                      <small>{cat.productCount} product{cat.productCount === 1 ? '' : 's'}</small>
                    </button>
                  ))}
                  {availableCategories.length === 0 && (
                    <p className="hb-add-empty">
                      Every category is already on the page. Create a new one in Catalog &gt; Categories.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {selected && (
            <div className="hb-panel" ref={inspectorRef}>
              <div className="hb-panel-title">{SECTION_LABELS[selected.type] || selected.type}</div>
              {renderInspector()}
            </div>
          )}
        </div>

        <div className="hb-panel hb-preview">
          <div className="hb-panel-title">Preview</div>
          <div className="hb-preview-frame">
            {sections.map(renderPreviewBlock)}
            {sections.length === 0 && <p className="hb-add-empty">Nothing to preview yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageDashboard;
